import React, { useState } from 'react'
import {
  Typography,
  withStyles,
  TextField,
} from '@material-ui/core'
import { Close as CloseIcon, Add as AddIcon } from '@material-ui/icons'
import { Icon } from '@iconify/react'
import styles from './styles'
import { CoreIconButton, CoreButton } from 'src/components/core'
import { track } from '@amplitude/analytics-browser'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import ContainerMediaGalery from 'src/container/image-gallery'
import SelectedIcon from 'src/components/selected-icon'
import { useDispatch } from 'react-redux'

const DEFAULT_FEATURE_ICON = 'material-symbols:check-rounded'
const HeaderSettingsCard = ({
  classes,
  themeSettings,
  onThemeSettingsChange,
}) => {
  const dispatch = useDispatch()

  // State for thumbnail image
  const [thumbnailImage, setThumbnailImage] = useState(
    themeSettings?.thumbnailImage ||
      'https://cdn.minicoursegenerator.com/assets/editor/header-card-thumbnail.jpg',
  )

  // State for features list
  const [features, setFeatures] = useState(
    themeSettings?.features || [
      { id: 1, text: 'Feature one', checked: true, icon: DEFAULT_FEATURE_ICON },
      { id: 2, text: 'Feature two', checked: true, icon: DEFAULT_FEATURE_ICON },
      { id: 3, text: 'Feature three', checked: true, icon: DEFAULT_FEATURE_ICON },
      { id: 4, text: 'Feature four', checked: true, icon: DEFAULT_FEATURE_ICON },
    ],
  )

  // Handle image upload
  const handleImageUpload = event => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = e => {
        const imageUrl = e.target.result
        setThumbnailImage(imageUrl)
        if (onThemeSettingsChange) {
          onThemeSettingsChange('thumbnailImage', imageUrl)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle feature toggle
  const handleFeatureToggle = featureId => {
    const updatedFeatures = features.map(feature =>
      feature.id === featureId
        ? { ...feature, checked: !feature.checked }
        : feature,
    )
    setFeatures(updatedFeatures)
    if (onThemeSettingsChange) {
      onThemeSettingsChange('features', updatedFeatures)
    }
  }

  // Handle feature text change
  const handleFeatureTextChange = (featureId, newText) => {
    const updatedFeatures = features.map(feature =>
      feature.id === featureId ? { ...feature, text: newText } : feature,
    )
    setFeatures(updatedFeatures)
    if (onThemeSettingsChange) {
      onThemeSettingsChange('features', updatedFeatures)
    }
  }

  // Handle feature removal
  const handleFeatureRemove = featureId => {
    const updatedFeatures = features.filter(feature => feature.id !== featureId)
    setFeatures(updatedFeatures)
    if (onThemeSettingsChange) {
      onThemeSettingsChange('features', updatedFeatures)
    }
  }

  // Handle adding new feature
  const handleAddFeature = () => {
    const newFeature = {
      id: Math.max(...features.map(f => f.id), 0) + 1,
      text: 'New feature',
      checked: true,
      icon: DEFAULT_FEATURE_ICON,
    }
    const updatedFeatures = [...features, newFeature]
    setFeatures(updatedFeatures)
    if (onThemeSettingsChange) {
      onThemeSettingsChange('features', updatedFeatures)
    }
  }

  // Handle feature icon change
  const handleFeatureIconChange = (featureId, iconName) => {
    const updatedFeatures = features.map(feature =>
      feature.id === featureId ? { ...feature, icon: iconName } : feature,
    )
    setFeatures(updatedFeatures)
    if (onThemeSettingsChange) {
      onThemeSettingsChange('features', updatedFeatures)
    }
  }

  // Open icon selector for a specific feature
  const openFeatureIconSelector = featureId => {
    const handleClickOutside = event => {
      const messageBox = document.getElementById('message-box-1')
      if (messageBox && !messageBox.contains(event.target)) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction('message-box-1'))
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
            <Typography>Select Icon</Typography>
          </div>`,
          buttons: [],
          component: (
            <SelectedIcon
              onIconSelect={icon => {
                handleFeatureIconChange(featureId, icon)
                dispatch(messageBoxHideAction('message-box-1'))
              }}
            />
          ),
          variant: MessageBoxVariant.DRAGGABLE,
          callback: () => {},
        }),
      ),
    )
  }

  // Open media gallery modal for thumbnail image
  const handleThumbnailImageChange = () => {
    track('thumbnail_media_gallery_clicked')

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24" />
              <Typography style="margin-left: 8; color: #002E47; fontWeight: 600; fontSize: 16;">
                Media Gallery
              </Typography>
            </div>
          `,
          component: (
            <ContainerMediaGalery
              onOk={items => {
                const imageUrl = items[0].url
                setThumbnailImage(imageUrl)
                if (onThemeSettingsChange) {
                  onThemeSettingsChange('thumbnailImage', imageUrl)
                }
                dispatch(messageBoxHideAction('message-box-1'))
              }}
              setImageURL={() => {}}
            />
          ),
          variant: MessageBoxVariant.DRAGGABLE,
          maxHeight: '640px',
        }),
      ),
    )
  }

  return (
    <div className={classes.cardSettingsContainer}>
      {/* Thumbnail Image Section */}
      <div className={classes.sectionContainer}>
        <Typography className={classes.sectionTitle}>
          Thumbnail image
        </Typography>
        <Typography className={classes.sectionDescription}>
          Upload an image to display at the top of the card.
        </Typography>

        <div className={classes.imageUploadContainer}>
          <div className={classes.imagePreview}>
            <img
              src={thumbnailImage}
              alt="Thumbnail preview"
              className={classes.previewImage}
            />
            <CoreIconButton
              className={classes.deleteButton}
              color="white"
              size="small"
              onClick={handleThumbnailImageChange}
            >
              <Icon icon="fa6-solid:rotate" width="20px" height="20px" />
            </CoreIconButton>
          </div>

          <Typography className={classes.uploadInfo}>
            Supported formats: JPG, or PNG. Maximum file size: 800K. Recommended
            min. size: 360 x 200 px.
          </Typography>
        </div>
      </div>

      {/* Features Section */}
      <div className={classes.sectionContainer}>
        <Typography className={classes.sectionTitle}>Features</Typography>
        <Typography className={classes.sectionDescription}>
          Only enter the numeric value. Currency symbol is automatically added.
        </Typography>

        <div className={classes.featuresContainer}>
          {features.map(feature => (
            <div key={feature.id} className={classes.featureItem}>
              <div className={classes.featureIconWrapper}>
                <CoreIconButton
                  onClick={() => openFeatureIconSelector(feature.id)}
                  size="small"
                  variant="text"
                  color="dark"
                  className={classes.featureIconButton}
                >
                  <Icon
                    icon={feature.icon || DEFAULT_FEATURE_ICON}
                    fontSize={20}
                    className={classes.checkIcon}
                  />
                </CoreIconButton>
              </div>
              <TextField
                value={feature.text}
                onChange={e =>
                  handleFeatureTextChange(feature.id, e.target.value)
                }
                className={classes.featureInput}
                variant="outlined"
                size="small"
              />
              <CoreIconButton
                onClick={() => handleFeatureRemove(feature.id)}
                className={classes.closeButton}
                size="small"
                color="dark"
                variant="text"
              >
                <CloseIcon />
              </CoreIconButton>
            </div>
          ))}

          <CoreButton
            variant="text"
            onClick={handleAddFeature}
            startIcon={<AddIcon />}
          >
            Add new feature
          </CoreButton>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(HeaderSettingsCard)
